if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cartCount = document.querySelector('cart-count');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');
        this.backInStockButton = this.querySelector('back-in-stock');
        this.productTitle = this.dataset.productTitle;
        this.statusMessage = this.querySelector(`#add-to-cart-status-${this.dataset.sectionId}`);

        // Determine if a size has already been selected by checking for the variant param
        const currentUrl = new URL(window.location.href);
        const currentUrlHasVariant = currentUrl.searchParams.has('variant');

        // Get the setting from data attribute
        this.requireVariantSelection = this.dataset.requireVariantSelection === 'true' && !currentUrlHasVariant;

        // Is the product product available for purchase
        this.productAvailable = this.dataset.productAvailable == 'true';

        this.hideBackInStock = this.dataset.backInStockEnabled === 'true' && this.requireVariantSelection;

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';

        // If we require size selection, disable the button initially and clear the variant
        if (this.requireVariantSelection) {
          // Force the button to be disabled on page load
          this.toggleSubmitButton(true, this.buttonLabel);

          // Clear any pre-selected variant if we're requiring explicit selection
          if (this.variantIdInput) {
            this.variantIdInput.value = '';
          }
        }

        if (this.hideBackInStock) {
          // Show the back in stock button if its available
          this.backInStockButton?.classList.add('hidden');
        }

        // Listen for variant selection changes
        document.addEventListener('variant:change', this.handleVariantChange.bind(this));
      }

      handleVariantChange(event) {
        // Only handle if the feature is enabled
        if (!this.requireVariantSelection) return;

        // Check if a variant is selected
        const variantId = this.variantIdInput.value;

        if (variantId && variantId !== '') {
          // Enable the button if a variant is selected
          this.toggleSubmitButton(false);
        } else {
          // Disable the button if no variant is selected
          this.toggleSubmitButton(true, this.buttonLabel);
        }
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        if (this.statusMessage) {
          this.statusMessage.textContent = '';
        }
        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.submitButtonText.classList.add('hidden');
        this.submitButton.querySelector('.loading-spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cartCount) {
          formData.append(
            'sections',
            this.cartCount.getSectionsToRender().map((section) => section.id),
          );
          formData.append('sections_url', window.location.pathname);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              if (this.cartCount && response.status === 422) {
                // Update cart count as we may have been limited by available inventory
                fetch(`${routes.cart_url}?section_id=cart-count`)
                  .then((response) => response.text())
                  .then((response) => {
                    this.cartCount.innerHTML = response;
                  })
                  .catch((e) => {
                    console.error(e);
                  });
              }

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cartCount) {
              window.location = window.routes.cart_url;
              return;
            }

            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              });
            this.error = false;
            if (this.statusMessage) {
              const message = this.productTitle
                ? `${this.productTitle} added to bag.`
                : window.variantStrings?.addedToCart || 'Item added to bag.';
              this.statusMessage.textContent = message;

              this.displayAddedToCartNotification(response);
            }

            this.cartCount.renderContents(response);
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.submitButton.querySelector('.loading-spinner').classList.add('hidden');
            this.submitButtonText.classList.remove('hidden');
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form-error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form-error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
          if (this.statusMessage) {
            this.statusMessage.textContent = ''; // Clear success message if error occurs
          }
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('aria-disabled', 'true');
          if (!/Select a size/.test(text)) {
            this.submitButton.setAttribute('tabindex', '-1');
          } else {
            this.submitButton.setAttribute('tabindex', '0');
          }

          if (text) {
            this.submitButtonText.textContent = text;
            this.submitButton.setAttribute('aria-label', text);
          }

          if (this.hideBackInStock) this.backInStockButton?.classList.add('hidden');
        } else {
          this.submitButton.removeAttribute('aria-disabled');
          this.submitButton.setAttribute('tabindex', '0');
          const defaultText = window.variantStrings?.addToCart || 'Add to bag';
          this.submitButtonText.textContent = defaultText;
          this.submitButton.setAttribute('aria-label', defaultText);
          this.backInStockButton?.classList.add('hidden');
        }
      }

      displayAddedToCartNotification(response) {
        // Create and append popup notification
        const notification = document.createElement('a');
        notification.setAttribute('href', '/cart');
        notification.className =
          'block fixed top-4 lg:top-5 right-4 lg:right-5 z-50 bg-white border transition-opacity duration-500 opacity-100 py-[10px] px-4 flex gap-5 items-center max-w-[27rem] drop-shadow-md';
        notification.style = 'width: calc(100% - 2rem);';
        const productImage = document.createElement('img');
        productImage.width = 100;
        productImage.src = response.featured_image.url + '&width=200';

        const textWrapper = document.createElement('div');
        textWrapper.className = 'flex-1 py-2 py-[15px]';

        const heading = document.createElement('h2');
        heading.className = 'uppercase font-semibold text-xs tracking-[1px]';
        heading.textContent = 'Added to bag';

        const subTextWrapper = document.createElement('div');
        subTextWrapper.className = 'flex gap-5 mt-[6px] lg:mt-2';

        const subSubTextWrapper = document.createElement('div');
        subSubTextWrapper.className = 'flex-1';

        const productTitle = document.createElement('div');
        productTitle.className = 'uppercase text-sm tracking-[1px]';
        const productName = response.title.split(' - ');
        productTitle.textContent = productName[0];

        const productColor = document.createElement('div');
        productColor.className = 'text-xs mt-[6px] lg:mt-2';
        productColor.textContent = productName[1];

        const productSize = document.createElement('div');
        productSize.className = 'text-xs mt-[6px] lg:mt-2';
        productSize.textContent = `Size: ${response.variant_options[0]}`;

        const price = document.createElement('div');
        price.className = 'text-sm font-medium';
        price.textContent = `$${response.presentment_price}`;

        subSubTextWrapper.appendChild(productTitle);
        subSubTextWrapper.appendChild(productColor);
        subSubTextWrapper.appendChild(productSize);

        subTextWrapper.appendChild(subSubTextWrapper);
        subTextWrapper.appendChild(price);

        textWrapper.appendChild(heading);
        textWrapper.appendChild(subTextWrapper);

        notification.appendChild(productImage);
        notification.appendChild(textWrapper);

        document.body.appendChild(notification);

        // Fade out after 3 seconds
        setTimeout(() => {
          notification.classList.remove('opacity-100');
          notification.classList.add('opacity-0');

          // Remove from DOM after fade completes
          setTimeout(() => {
            notification.remove();
          }, 500);
        }, 3000);
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }

      get buttonLabel() {
        return `${(this.productAvailable ? window.variantStrings?.selectVariant : window.variantStrings?.selectVariantForBIS) || 'Select a size'}`;
      }
    },
  );
}

// Prevent clicks on all buttons with aria-disabled="true"
document.addEventListener('click', (e) => {
  const target = e.target.closest('button[aria-disabled="true"]');
  if (target) {
    e.preventDefault();
    e.stopPropagation();
  }
});
